import { applyAdminQueryFlags, applyAdminQueryIdentity } from './admin-query-flags';

export type BootTheme = 'light' | 'dark';

export interface BootFeatureFlags {
  isAppAdmin?: boolean;
  isToRecordToFile?: boolean;
  showAdminDebugBanner?: boolean;
  conversationalAgent?: boolean;
}

export interface BootState {
  token?: string;
  sessionId?: string;
  theme: BootTheme;
  cif?: string;
  userName?: string;
  kmMeta?: string;
  featureFlags: BootFeatureFlags;
}

export interface SessionCacheEntry {
  sessionId: string;
  messages: unknown[];
  updatedAt: number;
}

declare global {
  interface Window {
    __GFH_BOOT__?: Partial<BootState>;
    __GFH_BOOT_STATE__?: BootState;
  }
}

const SESSION_CACHE_KEY = 'gfh_chatbot_session_cache';

const THEME_TOKENS: Record<BootTheme, Record<string, string>> = {
  light: {
    '--gfh-page-bg': '#000000',
    '--gfh-surface-bg': '#ffffff',
    '--gfh-input-bg': '#e6e7e9',
    '--gfh-text-primary': '#393939',
    '--gfh-text-secondary': '#616161',
    '--gfh-accent': '#275d75',
    '--gfh-skeleton-base': '#e6e7e9',
    '--gfh-skeleton-shine': '#f4f4f5',
  },
  dark: {
    '--gfh-page-bg': '#121212',
    '--gfh-surface-bg': '#1e1e1e',
    '--gfh-input-bg': '#2a2a2a',
    '--gfh-text-primary': '#f0f0f0',
    '--gfh-text-secondary': '#b0b0b0',
    '--gfh-accent': '#4a9ec4',
    '--gfh-skeleton-base': '#2a2a2a',
    '--gfh-skeleton-shine': '#3a3a3a',
  },
};

function parseBoolean(value: string | null | undefined): boolean {
  return value === 'true' || value === '1';
}

function parseFeatureFlags(params: URLSearchParams, source?: Partial<BootFeatureFlags>): BootFeatureFlags {
  let parsed: BootFeatureFlags = { ...source };

  const rawFlags = params.get('featureFlags') ?? params.get('flags');
  if (rawFlags) {
    try {
      parsed = { ...parsed, ...JSON.parse(rawFlags) };
    } catch {
      // Ignore malformed JSON and fall back to individual params.
    }
  }

  parsed = applyAdminQueryFlags(params, parsed);
  if (params.has('conversationalAgent')) {
    parsed.conversationalAgent = parseBoolean(params.get('conversationalAgent'));
  }

  return parsed;
}

function normalizeTheme(value: string | null | undefined): BootTheme {
  return value === 'dark' ? 'dark' : 'light';
}

function readQueryBootState(): Partial<BootState> {
  const params = new URLSearchParams(window.location.search);

  const boot: Partial<BootState> = {
    token: params.get('token')?.trim() || undefined,
    sessionId: params.get('sessionId')?.trim() || params.get('session_id')?.trim() || undefined,
    theme: normalizeTheme(params.get('theme')),
    featureFlags: parseFeatureFlags(params),
  };

  applyAdminQueryIdentity(params, boot);

  return boot;
}

function mergeBootState(...sources: Array<Partial<BootState> | undefined>): BootState {
  const merged: BootState = {
    theme: 'light',
    featureFlags: {},
  };

  for (const source of sources) {
    if (!source) {
      continue;
    }

    if (source.token) merged.token = source.token;
    if (source.sessionId) merged.sessionId = source.sessionId;
    if (source.theme) merged.theme = normalizeTheme(source.theme);
    if (source.cif) merged.cif = source.cif;
    if (source.userName) merged.userName = source.userName;
    if (source.kmMeta) merged.kmMeta = source.kmMeta;
    if (source.featureFlags) {
      merged.featureFlags = { ...merged.featureFlags, ...source.featureFlags };
    }
  }

  return merged;
}

export function applyBootTheme(theme: BootTheme = 'light'): void {
  const tokens = THEME_TOKENS[theme] ?? THEME_TOKENS.light;
  const root = document.documentElement;

  for (const [name, value] of Object.entries(tokens)) {
    root.style.setProperty(name, value);
  }

  root.dataset['gfhTheme'] = theme;

  if (document.body) {
    document.body.style.backgroundColor = tokens['--gfh-page-bg'];
  }
}

export function loadBootState(): BootState {
  const existing = window.__GFH_BOOT_STATE__;
  if (existing) {
    applyBootTheme(existing.theme);
    return existing;
  }

  const bootState = mergeBootState(readQueryBootState(), window.__GFH_BOOT__);
  applyBootTheme(bootState.theme);
  window.__GFH_BOOT_STATE__ = bootState;
  return bootState;
}

export function updateBootState(partial: Partial<BootState>): BootState {
  const next = mergeBootState(window.__GFH_BOOT_STATE__, partial);
  applyBootTheme(next.theme);
  window.__GFH_BOOT_STATE__ = next;
  return next;
}

export function listenForBootMessages(onUpdate: (boot: BootState) => void): () => void {
  const handler = (event: MessageEvent) => {
    const data = event.data;
    if (!data || typeof data !== 'object') {
      return;
    }

    const type = data.type ?? data.event;
    if (type !== 'GFH_BOOT' && type !== 'gfh-chatbot-boot' && type !== 'GFH_CHATBOT_BOOT') {
      return;
    }

    const payload = data.payload ?? data.boot ?? data;
    onUpdate(updateBootState(payload));
  };

  window.addEventListener('message', handler);
  return () => window.removeEventListener('message', handler);
}

export function loadCachedSession<T = unknown>(sessionId: string): T[] | null {
  if (!sessionId) {
    return null;
  }

  try {
    const raw = localStorage.getItem(SESSION_CACHE_KEY);
    if (!raw) {
      return null;
    }

    const cache = JSON.parse(raw) as SessionCacheEntry;
    if (cache.sessionId !== sessionId || !Array.isArray(cache.messages)) {
      return null;
    }

    return cache.messages as T[];
  } catch {
    return null;
  }
}

export function saveCachedSession(sessionId: string, messages: unknown[]): void {
  if (!sessionId) {
    return;
  }

  try {
    const cache: SessionCacheEntry = {
      sessionId,
      messages,
      updatedAt: Date.now(),
    };
    localStorage.setItem(SESSION_CACHE_KEY, JSON.stringify(cache));
  } catch {
    // Ignore storage failures (private mode, quota, etc.).
  }
}

export function deferAfterFirstPaint(task: () => void): void {
  requestAnimationFrame(() => {
    requestAnimationFrame(task);
  });
}
